import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '../../hooks/useQuery';
import { 
  Loader2, Users, Library, Copy, Check, Layout, Trophy, 
  Calendar, Hash, ArrowRight, MessageSquare, RefreshCw, 
  Shield, Award, Zap, AlertCircle, Clock
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { PageTitle } from '../../components/layout/PageTitle';
import { PageLayout } from '../../components/layout/PageLayout';
import { supabase } from '../../lib/supabase';
import { getOptimizedImageUrl, IMAGE_SIZES } from '../../lib/imageUtils';
import { getRelativeTimeString } from '../../utils/dateUtils';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

// Types
interface Profile {
  user_id: string;
  username: string;
  pokemon_id: string | null;
  avatar_url: string | null;
  created_at: string;
  is_pokemon_id_public: boolean;
  is_collection_public: boolean;
  pokemon_id_description: string | null;
}

interface PlayerStats {
  user_id: string;
  username: string;
  avatar_url: string | null;
  victories: number;
  defeats: number;
  total_matches: number;
  win_rate: number;
  rank: number;
}

interface Deck {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  is_public: boolean;
  card_count?: number;
  cards?: DeckCard[];
}

interface DeckCard {
  id: string;
  deck_id: string;
  card_id: number;
  quantity: number;
  card?: {
    id: number;
    card_fr: string;
    image: string;
    rarity: string;
  };
}

interface TradeCardData {
  id: string;
  card: {
    id: number;
    card_fr: string;
    image: string;
    rarity: string;
  };
}

interface Trade {
  id: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  created_at: string;
  user_id: string;
  wanted_card: {
    id: number;
    card_fr: string;
    image: string;
    rarity: string;
  };
  trade_cards: TradeCardData[];
}

// Définir les types pour les données retournées par Supabase
interface SupabaseCard {
  id: number;
  card_fr: string;
  image: string;
  rarity: string;
}

interface SupabaseTradeCard {
  id: number;
  card: SupabaseCard;
}

interface SupabaseTrade {
  id: number;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  created_at: string;
  user_id: string;
  wanted_card: SupabaseCard;
  trade_cards: SupabaseTradeCard[];
}

// Interface pour les statistiques d'échange
interface TradeStats {
  total: number;
  pending: number;
  accepted: number;
  completed: number;
  success_rate: number;
}

export function PublicProfile() {
  const { username } = useParams<{ username: string }>();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<boolean>(false);
  const [userDecks, setUserDecks] = useState<Deck[]>([]);
  const [activeTrades, setActiveTrades] = useState<Trade[]>([]);
  const [isLoadingDecks, setIsLoadingDecks] = useState(true);
  const [isLoadingTrades, setIsLoadingTrades] = useState(true);
  const [tradeStats, setTradeStats] = useState<TradeStats>({
    total: 0,
    pending: 0,
    accepted: 0,
    completed: 0,
    success_rate: 0
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, getPathForLang, lang } = useTranslation();

  const { data: profile, loading } = useQuery<Profile>(
    'profiles',
    { username },
    { single: true }
  );

  const { data: playerStats } = useQuery<PlayerStats>(
    'player_stats',
    { user_id: profile?.user_id },
    { single: true }
  );

  // Récupérer les decks de l'utilisateur
  useEffect(() => {
    if (profile?.user_id) {
      const fetchDecks = async () => {
        try {
          const { data, error } = await supabase
            .from('decks')
            .select(`
              id,
              name,
              description,
              created_at,
              updated_at,
              is_public,
              cards:deck_cards(
                id,
                deck_id,
                card_id,
                quantity,
                card:cards(
                  id,
                  card_fr,
                  image,
                  rarity
                )
              )
            `)
            .eq('user_id', profile.user_id)
            .eq('is_public', true)
            .order('updated_at', { ascending: false });

          if (error) throw error;

          // Transformer les données pour correspondre aux types Deck et DeckCard
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const decksWithCards: Deck[] = data?.map((deck: any) => {
            // Traiter les cartes
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const processedCards: DeckCard[] = (deck.cards || []).map((item: any) => {
              // Vérifier si card est un tableau et contient des éléments
              let cardData = null;

              if (Array.isArray(item.card) && item.card.length > 0) {
                cardData = item.card[0];
              } else if (item.card && typeof item.card === 'object' && !Array.isArray(item.card)) {
                cardData = item.card;
              }

              return {
                id: String(item.id),
                deck_id: String(item.deck_id),
                card_id: Number(item.card_id),
                quantity: Number(item.quantity),
                card: cardData ? {
                  id: Number(cardData.id),
                  card_fr: String(cardData.card_fr),
                  image: String(cardData.image),
                  rarity: String(cardData.rarity)
                } : undefined
              };
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }).filter((card: any) => card.card !== undefined);

            return {
              id: deck.id,
              name: deck.name,
              description: deck.description,
              created_at: deck.created_at,
              updated_at: deck.updated_at,
              is_public: deck.is_public,
              card_count: processedCards.length,
              cards: processedCards
            };
          }) || [];
          
          setUserDecks(decksWithCards);
        } catch (error) {
          console.error('Erreur lors du chargement des decks:', error);
        } finally {
          setIsLoadingDecks(false);
        }
      };

      fetchDecks();
    }
  }, [profile?.user_id]);

  // Fonction pour rendre le HTML dans les descriptions
  const renderDescription = (description: string | null) => {
    if (!description) return null;
    
    // Utiliser dangerouslySetInnerHTML pour rendre le HTML
    return <div dangerouslySetInnerHTML={{ __html: description.replace(/\n/g, '<br />') }} />;
  };

  // Récupérer les échanges actifs de l'utilisateur
  useEffect(() => {
    if (profile?.user_id) {
      const fetchTrades = async () => {
        try {
          const { data, error } = await supabase
            .from('trades')
            .select(`
              id,
              status,
              created_at,
              user_id,
              wanted_card:cards!trades_wanted_card_id_fkey(
                id,
                card_fr,
                image,
                rarity
              ),
              trade_cards:trade_cards!trade_cards_trade_id_fkey(
                id,
                card:cards!trade_cards_card_id_fkey(
                  id,
                  card_fr,
                  image,
                  rarity
                )
              )
            `)
            .eq('user_id', profile.user_id)
            .eq('status', 'pending')
            .order('created_at', { ascending: false });
            // Pas de limite pour afficher tous les échanges

          if (error) throw error;
          
          if (!data) {
            setActiveTrades([]);
            setIsLoadingTrades(false);
            return;
          }
          
          // Filtrer les échanges valides
          const validTrades = data.filter(trade => 
            trade && 
            trade.wanted_card && 
            trade.trade_cards
          ) as unknown as SupabaseTrade[];
          
          // Transformer les données pour correspondre au type Trade
          const formattedTrades: Trade[] = validTrades.map(trade => {
            return {
              id: String(trade.id),
              status: trade.status,
              created_at: trade.created_at,
              user_id: trade.user_id,
              wanted_card: {
                id: trade.wanted_card.id,
                card_fr: trade.wanted_card.card_fr,
                image: trade.wanted_card.image,
                rarity: trade.wanted_card.rarity
              },
              trade_cards: trade.trade_cards.map(tc => ({
                id: String(tc.id),
                card: {
                  id: tc.card.id,
                  card_fr: tc.card.card_fr,
                  image: tc.card.image,
                  rarity: tc.card.rarity
                }
              }))
            };
          });
          
          setActiveTrades(formattedTrades);
        } catch (error) {
          console.error('Erreur lors du chargement des échanges:', error);
        } finally {
          setIsLoadingTrades(false);
        }
      };

      fetchTrades();
    }
  }, [profile?.user_id]);

  // Récupérer les statistiques d'échange
  useEffect(() => {
    if (profile?.user_id) {
      const fetchTradeStats = async () => {
        try {
          // Requête pour les trades en attente
          const { data: pendingCount, error: pendingError } = await supabase
            .from('trades')
            .select('id', { count: 'exact' })
            .eq('user_id', profile.user_id)
            .eq('status', 'pending');

          // Requête pour les trades acceptés
          const { data: acceptedCount, error: acceptedError } = await supabase
            .from('trades')
            .select('id', { count: 'exact' })
            .eq('status', 'accepted')
            .or(`user_id.eq.${profile.user_id},accepted_by.eq.${profile.user_id}`);

          // Requête pour les trades complétés
          const { data: completedCount, error: completedError } = await supabase
            .from('trades')
            .select('id', { count: 'exact' })
            .eq('status', 'completed')
            .or(`user_id.eq.${profile.user_id},accepted_by.eq.${profile.user_id}`);

          if (pendingError || acceptedError || completedError) throw pendingError || acceptedError || completedError;

          // Calculer le total et le taux de succès
          const pending = pendingCount?.length || 0;
          const accepted = acceptedCount?.length || 0;
          const completed = completedCount?.length || 0;
          const total = pending + accepted + completed;
          
          // Calculer le taux de succès (échanges complétés / total des échanges non en attente)
          const nonPendingTotal = accepted + completed;
          const success_rate = nonPendingTotal > 0 ? Math.round((completed / nonPendingTotal) * 100) : 0;

          // Mettre à jour les statistiques
          setTradeStats({
            total,
            pending,
            accepted,
            completed,
            success_rate
          });
        } catch (error) {
          console.error('Erreur lors du chargement des statistiques d\'échange:', error);
        }
      };

      fetchTradeStats();
    }
  }, [profile?.user_id]);

  const copyToClipboard = async (code: string) => {
    try {
      // Vérifier si l'API Clipboard est disponible
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
        setCopiedId(code);
        setCopyError(false);
      } else {
        // Méthode de secours pour les navigateurs plus anciens
        const textArea = document.createElement('textarea');
        textArea.value = code;
        
        // Rendre l'élément invisible
        textArea.style.position = 'absolute';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
    setCopiedId(code);
          setCopyError(false);
        } else {
          setCopyError(true);
        }
      }
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
      setCopyError(true);
    }
    
    // Réinitialiser l'état après 2 secondes
    setTimeout(() => {
      setCopiedId(null);
      setCopyError(false);
    }, 2000);
  };

  const handleContactUser = async () => {
    if (!user || !profile) return;

    try {
      // Vérifier si une conversation existe déjà
      const { data: existingConversations, error: conversationsError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          conversations!inner(id)
        `)
        .eq('user_id', user.id);

      if (conversationsError) throw conversationsError;

      // Parmi ces conversations, trouver celle qui contient l'autre utilisateur
      const { data: existingConversation, error: findError } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', profile.user_id)
        .in('conversation_id', existingConversations?.map(c => c.conversation_id) || [])
        .maybeSingle();

      if (findError && findError.code !== 'PGRST116') throw findError;

      if (existingConversation) {
        // Si une conversation existe, rediriger vers celle-ci
        navigate(getPathForLang(`/messages/${existingConversation.conversation_id}`));
      } else {
        // Créer une nouvelle conversation
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (createError) throw createError;

        // Ajouter les participants
        const participants = [
          {
            conversation_id: newConversation.id,
            user_id: user.id
          },
          {
            conversation_id: newConversation.id,
            user_id: profile.user_id
          }
        ];

        const { error: participantsError } = await supabase
          .from('conversation_participants')
          .insert(participants);

        if (participantsError) throw participantsError;

        // Rediriger vers la nouvelle conversation
        navigate(getPathForLang(`/messages/${newConversation.id}`));
      }
    } catch (error) {
      console.error('Erreur lors de la gestion de la conversation:', error);
    }
  };

  if (loading || !profile) {
    return (
      <PageLayout disableAds={true}>
        <Helmet>
          <title>{t('public_profile.loading')} | Pokémon Pocket</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label={t('public_profile.loading')} />
        </div>
      </PageLayout>
    );
  }

  // Préparer les données structurées pour les decks
  const deckStructuredData = userDecks.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": userDecks.map((deck, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "CreativeWork",
        "name": deck.name,
        "description": deck.description || `Deck Pokémon créé par ${profile.username}`,
        "creator": {
          "@type": "Person",
          "name": profile.username
        },
        "dateCreated": deck.created_at,
        "dateModified": deck.updated_at
      }
    }))
  } : null;

  const dateLocale = lang === 'fr' ? fr : enUS;

  return (
    <PageLayout disableAds={true}>
      <Helmet>
        <title>{t('public_profile.seo.title', { username: profile.username })}</title>
        <meta name="description" content={t('public_profile.seo.description', { username: profile.username })} />
        <meta name="keywords" content={`${profile.username}, profil pokémon, collection cartes pokémon, decks pokémon, statistiques pvp pokémon`} />
        <link rel="canonical" href={window.location.href} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={t('public_profile.seo.title', { username: profile.username })} />
        <meta property="og:description" content={t('public_profile.seo.description', { username: profile.username })} />
        <meta property="og:url" content={window.location.href} />
        {profile.avatar_url && <meta property="og:image" content={profile.avatar_url} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={t('public_profile.seo.title', { username: profile.username })} />
        <meta name="twitter:description" content={t('public_profile.seo.description', { username: profile.username })} />
        {profile.avatar_url && <meta name="twitter:image" content={profile.avatar_url} />}
        
        {/* Données structurées pour le profil */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": profile.username,
            "image": profile.avatar_url || "",
            "url": window.location.href,
            "memberOf": {
              "@type": "Organization",
              "name": "Pokémon Pocket"
            },
            ...(playerStats ? {
              "description": `Joueur Pokémon avec ${playerStats.victories} victoires et un taux de victoire de ${playerStats.win_rate}%`,
              "knowsAbout": ["Pokémon", "Jeu de cartes à collectionner", "Stratégie"]
            } : {})
          })}
        </script>
        
        {/* Données structurées pour les decks */}
        {deckStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(deckStructuredData)}
          </script>
        )}
      </Helmet>
      <div className="min-h-screen bg-gray-100">
        <PageTitle title={t('public_profile.title', { username: profile.username || '' })} />
        
        {/* Header Banner avec gradient amélioré */}
        <div className="bg-gradient-to-r from-primary-700 to-primary-500">
          <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-8 sm:py-16">
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8">
              {/* Avatar avec bordure améliorée */}
              <div className="relative">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={`Avatar de ${profile.username}`}
                    className="w-20 h-20 sm:w-28 sm:h-28 rounded-full object-cover ring-4 ring-white shadow-lg"
                    loading="lazy"
                />
              ) : (
                  <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-white flex items-center justify-center ring-4 ring-white shadow-lg" aria-label="Avatar par défaut">
                    <Users className="w-10 h-10 sm:w-14 sm:h-14 text-primary-400" />
                  </div>
                )}
                {playerStats?.rank && playerStats.rank <= 10 && (
                  <div className="absolute -bottom-2 -right-2 bg-yellow-500 text-white rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center shadow-md" aria-label="Top 10 du classement">
                    <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
              )}
              </div>

              {/* User Info avec style amélioré */}
              <div className="text-center sm:text-left text-white flex-1 mt-2 sm:mt-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <h1 className="text-2xl sm:text-3xl font-bold mb-2">
                  {profile.username}
                </h1>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center text-primary-100 text-sm">
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <Calendar className="w-4 h-4" aria-hidden="true" />
                    <span>{t('public_profile.member_since', { 
                      time: formatDistanceToNow(new Date(profile.created_at), { 
                        addSuffix: false, 
                        locale: dateLocale 
                      })
                    })}</span>
                  </div>
                  {playerStats && (
                    <div className="flex items-center justify-center sm:justify-start gap-2">
                      <Trophy className="w-4 h-4" aria-hidden="true" />
                      <span>
                        {t('public_profile.stats.rank')} #{playerStats.rank}
                      </span>
                    </div>
                  )}
                </div>
                {user && user.id !== profile.user_id && (
                  <div className="mt-4">
                    <button
                      onClick={handleContactUser}
                      className="bg-white text-primary-700 py-2 px-4 rounded-lg font-medium shadow-sm hover:bg-primary-50 transition-colors inline-flex items-center gap-2"
                    >
                      <MessageSquare className="w-4 h-4" />
                      {t('public_profile.contact')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content avec design amélioré */}
        <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 -mt-4 sm:-mt-8">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 p-3 sm:p-6">
              {/* Colonne de gauche */}
              <div className="space-y-4 sm:space-y-6">
                {/* Carte de statistiques PvP améliorée */}
                {playerStats && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300">
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-yellow-500" />
                        {t('public_profile.stats.title')} PvP
                      </h2>
                    </div>
                    <div className="p-4 sm:p-6">
                      <div className="mb-6 flex flex-col sm:flex-row items-center gap-4">
                        <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg p-4 text-white w-full sm:w-auto">
                          <div className="text-2xl sm:text-3xl font-bold text-center sm:text-left">
                            {playerStats.rank ? playerStats.rank : '-'}
                          </div>
                          <div className="text-xs text-primary-100 text-center sm:text-left">{t('public_profile.stats.rank')}</div>
                        </div>
                        <div className="w-full sm:w-auto text-center sm:text-left">
                          <div className="text-xl sm:text-2xl font-bold text-gray-900">
                            {playerStats.win_rate}%
                          </div>
                          <div className="text-sm text-gray-500">{t('public_profile.stats.win_rate')}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 sm:gap-4">
                        <StatBlock 
                          label={t('public_profile.stats.victories')}
                          value={playerStats.victories} 
                          icon={<Shield className="w-4 h-4 text-green-500" />}
                        />
                        <StatBlock 
                          label={t('public_profile.stats.defeats')}
                          value={playerStats.defeats} 
                          icon={<Zap className="w-4 h-4 text-red-500" />}
                        />
                        <StatBlock 
                          label={t('public_profile.stats.total_matches')}
                          value={playerStats.total_matches} 
                          icon={<Award className="w-4 h-4 text-blue-500" />}
                        />
                      </div>
                      <div className="mt-4 text-center">
                        <Link 
                          to={getPathForLang("/pvp/ranking")}
                          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 font-medium text-sm"
                        >
                          <Trophy className="w-4 h-4" />
                          {t('public_profile.stats.rank_link', { fallback: 'Voir le classement PvP complet' })}
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}

                {/* Statistiques d'échange */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-primary-500" />
                      {t('public_profile.trades.stats_title')}
                    </h2>
                    {/* Trade stats summary */}
                    {Object.values(tradeStats).some(stat => stat > 0) && (
                      <div className="mt-4 grid grid-cols-4 gap-2 text-xs text-center">
                        <div>
                          <div className="font-semibold">{tradeStats.total}</div>
                          <div className="text-gray-500">{t('public_profile.stats.trades')}</div>
                        </div>
                        <div>
                          <div className="font-semibold">{tradeStats.pending}</div>
                          <div className="text-gray-500">{t('public_profile.stats.pending_trades')}</div>
                        </div>
                        <div>
                          <div className="font-semibold">{tradeStats.completed}</div>
                          <div className="text-gray-500">{t('public_profile.stats.completed_trades')}</div>
                        </div>
                        <div>
                          <div className="font-semibold">{tradeStats.success_rate}%</div>
                          <div className="text-gray-500">{t('public_profile.stats.success_rate')}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 sm:p-6">
                    <div className="mb-6 flex flex-col sm:flex-row items-center gap-4">
                      <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg p-4 text-white w-full sm:w-auto">
                        <div className="text-2xl sm:text-3xl font-bold text-center sm:text-left">
                          {tradeStats.success_rate}%
                        </div>
                        <div className="text-xs text-primary-100 text-center sm:text-left">{t('public_profile.stats.success_rate')}</div>
                      </div>
                      <div className="w-full sm:w-auto text-center sm:text-left">
                        <div className="text-xl sm:text-2xl font-bold text-gray-900">
                          {tradeStats.total}
                        </div>
                        <div className="text-sm text-gray-500">{t('public_profile.stats.trades')}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-4">
                      <StatBlock 
                        label={t('public_profile.stats.pending_trades')}
                        value={tradeStats.pending} 
                        icon={<Clock className="w-4 h-4 text-yellow-500" />}
                      />
                      <StatBlock 
                        label={t('public_profile.stats.accepted_trades')}
                        value={tradeStats.accepted} 
                        icon={<Check className="w-4 h-4 text-green-500" />}
                      />
                      <StatBlock 
                        label={t('public_profile.stats.completed_trades')}
                        value={tradeStats.completed} 
                        icon={<Award className="w-4 h-4 text-blue-500" />}
                      />
                    </div>
                    <div className="mt-4 text-center">
                      <Link 
                        to={getPathForLang("/trades/browse")}
                        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 font-medium text-sm"
                      >
                        <RefreshCw className="w-4 h-4" />
                        {t('public_profile.trades.browse')}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Code Ami avec design amélioré */}
              {profile.is_pokemon_id_public && profile.pokemon_id && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300">
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Hash className="w-5 h-5 text-primary-500" />
                        {t('public_profile.friend_code.title')}
                    </h2>
                  </div>
                    <div className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row items-center gap-3">
                        <code className="flex-1 px-3 sm:px-4 py-3 bg-gray-50 rounded-xl font-mono text-base sm:text-lg text-center border border-gray-200 w-full">
                        {profile.pokemon_id}
                      </code>
                      <button
                          onClick={() => copyToClipboard(profile.pokemon_id || '')}
                        className={`p-3 rounded-xl transition-all ${
                            copyError 
                              ? 'bg-red-100 text-red-600 scale-110'
                              : copiedId === profile.pokemon_id
                            ? 'bg-green-100 text-green-600 scale-110'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                        }`}
                          aria-label={
                            copyError 
                              ? t('public_profile.friend_code.error', { fallback: "Erreur lors de la copie" })
                              : copiedId === profile.pokemon_id 
                                ? t('public_profile.friend_code.copied')
                                : t('public_profile.friend_code.copy')
                          }
                        >
                          {copyError ? (
                            <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
                          ) : copiedId === profile.pokemon_id ? (
                            <Check className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
                          ) : (
                            <Copy className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                      
                      {/* Message de feedback pour la copie */}
                      {(copiedId === profile.pokemon_id || copyError) && (
                        <div className={`mt-2 text-sm text-center font-medium ${
                          copyError ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {copyError 
                            ? t('public_profile.friend_code.error_message', { fallback: "Erreur lors de la copie. Veuillez réessayer." })
                            : t('public_profile.friend_code.copied_message', { fallback: "Code ami copié dans le presse-papiers !" })}
                        </div>
                      )}
                      
                    {profile.pokemon_id_description && (
                        <div className="mt-4 text-sm text-gray-600 bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <p className="italic">"{profile.pokemon_id_description}"</p>
                        </div>
                    )}
                  </div>
                </div>
              )}
            </div>

              {/* Colonne de droite (plus large) */}
            <div className="lg:col-span-2 space-y-6">
                

                {/* Collection avec design amélioré */}
              {profile.is_collection_public && (
                <Link 
                  to={getPathForLang(`/users/${username}/collection`)}
                  className="block bg-white rounded-xl shadow-sm border border-gray-200 hover:border-primary-500 hover:shadow-md transition-all duration-300 overflow-hidden"
                  aria-label={t('public_profile.collection.view_aria', { username: profile.username })}
                >
                  <div className="px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="bg-primary-100 text-primary-600 p-2 sm:p-3 rounded-lg">
                        <Library className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
                      </div>
                      <div>
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900">{t('public_profile.collection.title')}</h2>
                        <p className="text-xs sm:text-sm text-gray-500">{t('public_profile.collection.description', { username: profile.username })}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" aria-hidden="true" />
                  </div>
                </Link>
              )}
                
                {/* Nouvelle section: Échanges en attente */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-primary-500" />
                      {t('public_profile.trades.title')}
                    </h2>
                  </div>
                  <div className="p-4 sm:p-6">
                    {isLoadingTrades ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                      </div>
                    ) : activeTrades.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {activeTrades.map(trade => (
                          <Link 
                            key={trade.id}
                            to={getPathForLang(`/trades/${trade.id}/info`)}
                            className="block border border-gray-100 rounded-lg p-3 hover:bg-gray-50 transition-colors h-full"
                          >
                            <div className="flex flex-col h-full">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-10 h-14 sm:w-12 sm:h-16">
                                    <img
                                      src={getOptimizedImageUrl(trade.wanted_card?.image || '', IMAGE_SIZES.THUMBNAIL).replace('https://image.pokemonpocket.fr/', 'https://image.pokemonpocket.fr/preview/')}
                                      alt={trade.wanted_card?.card_fr || t('public_profile.trades.unknown_card', { fallback: 'Carte inconnue' })}
                                      className="w-full h-full object-contain rounded-lg"
                                      loading="lazy"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.onerror = null;
                                        target.src = '/placeholder-card.png';
                                      }}
                                    />
                                  </div>
                                  <div className="overflow-hidden">
                                    <h3 className="text-xs sm:text-sm font-medium text-gray-900 truncate">{trade.wanted_card?.card_fr || t('public_profile.trades.unknown_card', { fallback: 'Carte inconnue' })}</h3>
                                    <p className="text-xs text-gray-500">{trade.wanted_card?.rarity || ''}</p>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full whitespace-nowrap">
                                  {getRelativeTimeString(new Date(trade.created_at), t)}
                                </span>
                              </div>

                              <div className="flex-grow">
                                <h4 className="text-xs font-medium text-gray-500 mb-2">{t('public_profile.trades.offered_cards', { fallback: 'Cartes proposées :' })}</h4>
                                <div className="grid grid-cols-4 gap-1 grid-rows-[repeat(2,_minmax(0,_1fr))]">
                                  {trade.trade_cards.slice(0, trade.trade_cards.length > 8 ? 7 : 8).map((tc) => (
                                    <div key={tc.id} className="relative group">
                                      <img
                                        src={getOptimizedImageUrl(tc.card?.image || '', IMAGE_SIZES.THUMBNAIL).replace('https://image.pokemonpocket.fr/', 'https://image.pokemonpocket.fr/preview/')}
                                        alt={tc.card?.card_fr || t('public_profile.trades.unknown_card', { fallback: 'Carte inconnue' })}
                                        className="w-full aspect-[3/4] object-contain rounded-md"
                                        loading="lazy"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.onerror = null;
                                          target.src = '/placeholder-card.png';
                                        }}
                                      />
                                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity duration-200 rounded-md flex items-center justify-center">
                                        <span className="text-white text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center px-1">
                                          {tc.card?.card_fr || t('public_profile.trades.unknown_card', { fallback: 'Carte inconnue' })}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                  {trade.trade_cards.length > 8 && (
                                    <div className="relative flex items-center justify-center bg-gray-100 aspect-[3/4] rounded-md">
                                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded-md">
                                        <span className="text-gray-700 text-xs">
                                          +{trade.trade_cards.length - 7}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="mt-3">
                                <div
                                  className="block w-full py-2 px-3 bg-primary-100 hover:bg-primary-200 text-primary-700 text-center text-xs sm:text-sm font-medium rounded-lg transition-colors"
                                >
                                  {t('public_profile.trades.view_trade', { fallback: 'Voir l\'échange' })}
                                </div>
                              </div>
                  </div>
                </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <RefreshCw className="w-12 h-12 mx-auto text-gray-300 mb-3" aria-hidden="true" />
                        <p>{t('public_profile.trades.none')}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Decks publics avec design amélioré */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Layout className="w-5 h-5 text-primary-500" />
                      Decks publics
                    </h2>
                  </div>
                  <div className="p-4 sm:p-6">
                    {isLoadingDecks ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                      </div>
                    ) : userDecks.length > 0 ? (
                      <div className="space-y-4">
                        {userDecks.map(deck => (
                          <Link 
                            key={deck.id}
                            to={getPathForLang(`/decks/${deck.id}`)}
                            className="block bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group"
                          >
                            <div className="p-4 sm:p-6">
                              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-6">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                      <Layout className="w-3 h-3 sm:w-4 sm:h-4 text-primary-500" />
                                    </div>
                                    <div>
                                      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                                        {deck.name}
                                      </h2>
                                    </div>
                                  </div>
                                  {deck.description && (
                                    <p className="text-xs sm:text-sm text-gray-600 mt-2 line-clamp-2">
                                      {renderDescription(deck.description)}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span>
                                      {t('public_profile.decks.updated')} {new Date(deck.updated_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Library className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span>{deck.card_count || 0} {t('public_profile.decks.cards')}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Aperçu des cartes du deck */}
                            {deck.cards && deck.cards.length > 0 && (
                              <div className="px-4 sm:px-6 pb-4">
                                <div className="flex overflow-x-auto gap-2 py-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                  {deck.cards.slice(0, 5).map((card) => (
                                    <div key={card.id} className="flex-shrink-0 relative group">
                                      <div className="w-14 sm:w-16 h-20 sm:h-22 relative">
                                        {card.card && (
                                          <>
                                            <img
                                              src={getOptimizedImageUrl(card.card.image, IMAGE_SIZES.THUMBNAIL).replace('https://image.pokemonpocket.fr/', 'https://image.pokemonpocket.fr/preview/')}
                                              alt={card.card.card_fr}
                                              className="w-full h-full object-contain rounded-lg"
                                              loading="lazy"
                                              onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.onerror = null;
                                                target.src = '/placeholder-card.png';
                                              }}
                                            />
                                            {card.quantity > 1 && (
                                              <div className="absolute -bottom-1 -right-1 bg-primary-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                                {card.quantity}
                                              </div>
                                            )}
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                                              <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-center px-1">
                                                {card.card.card_fr}
                                              </span>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  {deck.cards.length > 5 && (
                                    <div className="flex-shrink-0 w-14 sm:w-16 h-20 sm:h-22 flex items-center justify-center bg-gray-100 rounded-lg">
                                      <span className="text-gray-600 text-xs sm:text-sm font-medium">
                                        +{deck.cards.length - 5}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-4 sm:px-6 py-3 flex items-center justify-between text-xs sm:text-sm font-medium">
                              <span className="text-primary-600">{t('public_profile.decks.view_deck')}</span>
                              <span className="text-primary-600 transform group-hover:translate-x-1 transition-transform">→</span>
                            </div>
                          </Link>
                        ))}
                        
              <Link 
                to={getPathForLang(`/public-decks?user_id=${profile.user_id}`)}
                          className="block text-center text-sm text-primary-600 hover:text-primary-800 font-medium mt-6"
                        >
                          {t('public_profile.decks.view_all')}
                        </Link>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Layout className="w-12 h-12 mx-auto text-gray-300 mb-3" aria-hidden="true" />
                        <p>{t('public_profile.decks.none')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

// Composant StatBlock amélioré
function StatBlock({ 
  label, 
  value, 
  icon 
}: { 
  label: string; 
  value: number; 
  icon?: React.ReactNode 
}) {
  return (
    <div className="bg-gray-50 rounded-xl p-3 sm:p-4 text-center border border-gray-100">
      <div className="flex justify-center mb-1">
        {icon}
      </div>
      <div className="text-lg sm:text-xl font-bold text-gray-900">{value}</div>
      <div className="text-[10px] sm:text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}
